import { useRef, useEffect, useState, ReactNode } from 'react';
import cn from 'classnames';

import styles from './ContentContainer.module.scss';

export interface FilterContentContainerProps {
  searchComponent?: ReactNode;
  actionsComponent?: ReactNode;
  badgesComponent?: ReactNode;
  children: ReactNode;
  className?: string;
}

export const ContentContainer: React.FC<FilterContentContainerProps> = ({
  searchComponent,
  actionsComponent,
  badgesComponent,
  children,
  className,
}) => {
  const topSectionRef = useRef<HTMLDivElement>(null);
  const [topHeight, setTopHeight] = useState(0);

  useEffect(() => {
    const topElement = topSectionRef.current;
    if (!topElement) return;

    // Create ResizeObserver to watch for height changes
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const height = entry.contentRect.height;
        setTopHeight(height);
      }
    });

    // Start observing
    resizeObserver.observe(topElement);

    // Initial height measurement
    setTopHeight(topElement.offsetHeight);

    // Cleanup
    return () => {
      resizeObserver.disconnect();
    };
  }, [searchComponent, actionsComponent, badgesComponent]);

  const hasTopContent = searchComponent || actionsComponent || badgesComponent;

  return (
    <div className={cn(styles.filterContentContainer, className)}>
      {hasTopContent && (
        <div ref={topSectionRef} className={styles.topSection}>
          {searchComponent && (
            <div className={styles.searchWrapper}>{searchComponent}</div>
          )}
          {actionsComponent && (
            <div className={styles.actionsWrapper}>{actionsComponent}</div>
          )}
          {badgesComponent && (
            <div className={styles.badgesWrapper}>{badgesComponent}</div>
          )}
        </div>
      )}
      <div
        className={styles.contentSection}
        style={
          {
            '--top-section-height': `${topHeight}px`,
          } as React.CSSProperties
        }
      >
        {children}
      </div>
    </div>
  );
};
